import { inject } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    CanActivateFn,
    CanMatchFn,
    Route,
    Router,
    RouterStateSnapshot,
    UrlSegment,
    UrlTree
} from '@angular/router';
import { RouteAccessKey } from './tenant-config.model';
import { TenantConfigService } from './tenant-config.service';

/**
 * Guard de activación.
 *
 * Se aplica con `canActivate: [tenantRouteGuard]` y lee la key desde
 * `route.data['tenantRoute']` (o, como fallback, normaliza la URL).
 *
 * Si la ruta no está habilitada para el tenant, redirige a `/access-denied`
 * conservando la URL original en `?returnUrl=...`.
 */
export const tenantRouteGuard: CanActivateFn = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
) => {
    const router = inject(Router);
    const tenantConfig = inject(TenantConfigService);

    const routeKey = getRouteAccessKey(route, state);

    if (!routeKey) {
        return true;
    }

    if (tenantConfig.canAccessRoute(routeKey)) {
        return true;
    }

    return router.createUrlTree(['/app/principal'], {
        queryParams: {
            returnUrl: normalizeUrl(state.url)
        }
    });
};

/**
 * Factory que devuelve un `CanMatchFn` parametrizado con la `RouteAccessKey`.
 *
 * Pensado para usar en rutas con `loadChildren`/`loadComponent` y EVITAR que
 * el bundle se descargue cuando la ruta no está habilitada para el tenant.
 *
 * Si la ruta está deshabilitada, retorna un `UrlTree` apuntando a
 * `/access-denied` con la URL original en `?returnUrl=...`.
 */
export function tenantRouteMatch(routeKey: RouteAccessKey): CanMatchFn {
    return (_route: Route, _segments: UrlSegment[]): boolean | UrlTree => {
        const router = inject(Router);
        const tenantConfig = inject(TenantConfigService);

        if (tenantConfig.canAccessRoute(routeKey)) {
            return true;
        }

        // Usamos routeKey directamente (full path como '/app/principal') en lugar
        // de reconstruir desde los segments relativos del child, que no incluyen
        // el prefijo del módulo padre (p.ej. solo darían '/principal' en vez de '/app/principal').
        return router.createUrlTree(['/app/principal'], {
            queryParams: { returnUrl: routeKey }
        });
    };
}

function getRouteAccessKey(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
): RouteAccessKey | null {
    const configuredRouteKey = route.data?.['tenantRoute'] as RouteAccessKey | undefined;

    if (configuredRouteKey) {
        return configuredRouteKey;
    }

    return normalizeUrl(state.url) as RouteAccessKey;
}

function normalizeUrl(url: string): string {
    const cleanUrl = url.split('?')[0].split('#')[0];

    if (cleanUrl.length > 1 && cleanUrl.endsWith('/')) {
        return cleanUrl.slice(0, -1);
    }

    return cleanUrl || '/';
}

/**
 * Orden de prioridad para resolver la ruta de aterrizaje al entrar a `/app`.
 *
 * Cada entrada apunta al destino final (ya con redirects internos resueltos),
 * para evitar caer en un `redirectTo` hacia una sub-ruta deshabilitada.
 *
 * El guard recorre la lista y usa la PRIMERA ruta cuyo `canAccessRoute()`
 * devuelva `true`. Si ninguna pasa, redirige a `/access-denied`.
 */
const DEFAULT_ROUTE_PRIORITY: RouteAccessKey[] = [
    '/app/principal',
    '/app/casos/listado',
    '/app/gestion/agenda',
    '/app/atenciones/listado',
    '/app/tareas/listado',
    '/app/admisiones/dashboard',
    '/app/gestion-de-datos',
    '/app/prestadores/buscar-prestadores',
    '/app/gestion/control-prestacional',
    '/app/gestion/consolidacion',
    '/app/perfil'
];

/**
 * Guard que resuelve la entrada a `/app` cuando no hay sub-ruta explícita.
 *
 * Reemplaza el viejo `{ path: '', redirectTo: 'principal' }` para no aterrizar
 * en `/access-denied` cuando `/app/principal` está deshabilitada.
 *
 * Estrategia:
 *  - Recorre `DEFAULT_ROUTE_PRIORITY` en orden.
 *  - Devuelve `UrlTree` a la primera ruta con `FeatureConfig.ENABLED`.
 *  - Si ninguna está habilitada, redirige a `/access-denied`.
 */
export const tenantDefaultRouteGuard: CanActivateFn = () => {
    const router = inject(Router);
    const tenantConfig = inject(TenantConfigService);

    const firstAccessibleRoute = tenantConfig.getFirstAccessibleDefaultRoute();

    if (firstAccessibleRoute) {
        return router.createUrlTree([firstAccessibleRoute]);
    }

    return router.createUrlTree(['/app/principal']);
};